import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useMemo, useState, useEffect } from "react";

import {
  Alert,
  Box,
  Button,
  Stack,
  Flex,
  HTMLWrapper
} from "@/storybook-components";
import { useAppSelector } from "@/redux/store/hooks";

import useEmbeds from "@/hooks/useEmbeds";

import SurveyResults from "./results";
import usePinned from "@/hooks/usePinned";
import { getQuantifiableQuestions } from "@/helpers/survey";
import useUser from "@/hooks/useUser";
import useAuthGate from "@/hooks/useAuthGate";

const SurveyResponse = () => {
  const router = useRouter();
  const { isEmbed } = useEmbeds();
  const { isPinned } = usePinned();
  const { translate } = useTranslator();
  const [showResponses, setShowResponses] = useState(false);
  const survey = useAppSelector((state) => state.survey);
  const { project } = useAppSelector((state) => state.project);
  const { isPreviewUser } = useUser();
  const { isGuestParticipationEnabled, suggestProfileCompletion } =
    useAuthGate();

  const { showResults, thanksMsg, allowUnverifiedParticipation } =
    survey.survey;
  const guestParticipationAllowed =
    isGuestParticipationEnabled && allowUnverifiedParticipation;
  const hasCustomThanksMsg = Boolean(thanksMsg?.trim());

  useEffect(() => {
    if (guestParticipationAllowed) {
      suggestProfileCompletion({
        reason: "profileGate.partialSuggestion",
        source: "survey"
      });
    }
  }, [guestParticipationAllowed, suggestProfileCompletion]);

  const allQuantifidQuestions = useMemo(
    () => getQuantifiableQuestions(survey),
    [survey]
  );

  const { visibilityMode } = project;

  const showResponseNode = () => {
    if (!showResults || !allQuantifidQuestions.length || isPreviewUser)
      return null;

    const viewResponsesButton = () => (
      <Button
        variant="primary"
        size={{ base: "lg", lg: "md" }}
        onClick={() => setShowResponses(true)}
        data-testid="survey-participant-response-button-view-results"
      >
        {translate("survey.viewResponses")}
      </Button>
    );

    if (isEmbed) {
      if (visibilityMode === "Public") return viewResponsesButton();
    } else return viewResponsesButton();
  };

  const thanksMessageNode = () => {
    if (hasCustomThanksMsg) {
      return (
        <Box
          p={4}
          bg="white"
          width="100%"
          borderRadius="md"
          data-testid="survey-participant-response-custom-thanks"
        >
          <HTMLWrapper html={thanksMsg} />
        </Box>
      );
    }

    return (
      <Alert.Root
        p={4}
        gap={0}
        status="success"
        borderRadius="md"
        textAlign="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Alert.Indicator m="auto" boxSize={12} />
        <Alert.Title mt={4} fontSize="lg">
          {translate("survey.thanksForTakingSurvey")}
        </Alert.Title>
      </Alert.Root>
    );
  };

  const successNode = () => {
    return (
      <>
        {thanksMessageNode()}
        <Stack
          mt={{ base: 9, md: 12 }}
          direction={{ sm: "column" }}
          width={{ md: "auto", sm: "full" }}
          alignSelf={{ md: "center", sm: "flex-start" }}
        >
          {!isEmbed && !isPinned && (
            <Button
              variant="secondary"
              size={{ base: "lg", lg: "md" }}
              onClick={() => router.push(`/${router.query?.projectId}`)}
              data-testid="survey-participant-response-button-back-to-project"
            >
              {translate("survey.backToProjectPage")}
            </Button>
          )}
          {showResponseNode()}
        </Stack>
      </>
    );
  };

  return (
    <Flex
      flexDirection="column"
      justify={{ md: "start", sm: "space-between" }}
      minHeight={{ md: "auto", sm: "calc(100vh - 250px)" }}
      data-testid="survey-participant-response"
    >
      {showResponses ? <SurveyResults /> : successNode()}
    </Flex>
  );
};

export default SurveyResponse;
